import React, { useEffect, useState, useCallback } from 'react';
import { UserEntity } from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import Pagination from 'Views/Components/Pagination/Pagination';
import If from 'Views/Components/If/If';
import { TextInput } from 'Views/Components/New/TextInput';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { AccountDetailsTabHeader } from '../AccountDetailsTabHeader';
import { VehiclesList } from './VehiclesList';
import { getUserVehicles, UserVehiclesResponse } from 'Services/Api/_HumanWritten/UserVehicleService';
import { getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { openVehicleCreateModal } from './VehicleEditModal';
import CargoProfilesSection from './CargoProfilesSection';

interface AccountDetailsVehiclesProps {
	user?: UserEntity;
	staffView?: boolean;
}

const PER_PAGE = 5;

function AccountDetailsVehicles({ user, staffView }: AccountDetailsVehiclesProps) {
	const [pageNo, setPageNo] = useState<number>(0);
	const [vehiclesData, setVehiclesData] = useState<UserVehiclesResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [searchInput, setSearchInput] = useState<string>('');
	const [search, setSearch] = useState<string>('');

	// Debounce the search box so we don't hit the server on every keystroke; reset to the first page
	// whenever the applied search changes.
	useEffect(() => {
		const handle = setTimeout(() => {
			setSearch(searchInput.trim());
			setPageNo(0);
		}, 300);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const fetchVehicles = useCallback(async () => {
		if (!isNotNullOrUndefined(user)) {
			return;
		}

		setIsLoading(true);
		try {
			const data = await getUserVehicles(user.id, pageNo * PER_PAGE, PER_PAGE, search);
			setVehiclesData(data);
		} catch (error) {
			console.error('Failed to fetch vehicles:', error);
			setVehiclesData({ vehicles: [], count: 0 });
		} finally {
			setIsLoading(false);
		}
	}, [user?.id, pageNo, search]);

	useEffect(() => {
		fetchVehicles();
	}, [fetchVehicles]);

	const handleRemove = (vehicleId: string) => {
		// Refresh the list after removal
		fetchVehicles();
	};

	const handleUpdate = () => {
		// Refresh the list after update
		fetchVehicles();
	};

	const vehicleLabel = getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle');

	const handleAdd = () => {
		if (!user) return;
		openVehicleCreateModal(user.id, fetchVehicles).catch(() => {
			// Modal was cancelled
		});
	};

	return (
		<>
			<If condition={search.trim().length > 0 || (vehiclesData?.count ?? 0) > 0}>
				<div className="account-page-vehicles__search">
					<TextInput
						value={searchInput}
						onChange={event => setSearchInput(event.target.value)}
						placeholder={`Search by ${getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego').toLowerCase()}, make or model`}
						startAdornment={<Icon name="search" useMaterialIcons classname="text-base leading-none" />}
					/>
				</div>
			</If>
			<div className="fake-table__header">
				{vehicleLabel}s list
				<Button
					icon={{ icon: 'plus', iconPos: 'icon-only' }}
					display={Display.Outline}
					colors={Colors.Secondary}
					onClick={handleAdd}
					labelVisible={false}
					className="fake-table__header-add-btn"
					newStyles
					buttonProps={{ 'data-test-id': 'vehicle-library-add-button' } as any}
				/>
			</div>
			<VehiclesList
				vehicles={vehiclesData?.vehicles ?? null}
				isLoading={isLoading}
				onRemove={handleRemove}
				onUpdate={handleUpdate}
			/>
			<If condition={isNotNullOrUndefined(vehiclesData) && vehiclesData!.count > PER_PAGE}>
				<div className="collection-component account-page-vehicles__pagination">
					<section className="collection__pagination new-style">
						<section className="collection__load">
							<Pagination
								perPage={PER_PAGE}
								pageNo={pageNo}
								totalRecords={vehiclesData?.count ?? 0}
								onPageChange={pageNum => {
									setPageNo(pageNum);
								}}
								showGoToPageBox={false}
								showFirstArrow={false}
								showLastArrow={false}
								leftArrowIcon="arrow-left"
								rightArrowIcon="arrow-right"
							/>
						</section>
					</section>
				</div>
			</If>
			<If condition={!!staffView}>
				<CargoProfilesSection user={user} />
			</If>
		</>
	);
}

export default AccountDetailsVehicles;
